import Container from "../Container";
import SectionHeader from "../SectionHeader";
import { useEffect, useRef, useState } from "react";
import { useInView, useAnimation } from "framer-motion";
import { useIsFirefox } from "hooks/useFireFox";
import Faqpage from "../FaqPage/Faqpage";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { AnimatedGradientBorder, GlowCard } from "../shared";
import { gradientRowBgClass } from "screens/Dashboard/utils/classes";

const FAQSection = ({ isDashboard }: { isDashboard?: boolean }) => {
  const { t } = useTranslation();
  const refMobile = useRef(null);
  const containerRef = useRef(null);
  const sectionRef = useRef(null);
  const controlsWheel = useAnimation();

  const isMobileInView = useInView(refMobile, { once: true });
  const isSectionInView = useInView(sectionRef, { once: true });

  const [currentStep, setCurrentStep] = useState<number>(0);
  const [stepPositions, setStepPositions] = useState<number[]>([]);

  const controlsMobile = useAnimation();
  const controlsDesktop = useAnimation();

  const isFireFox = useIsFirefox();

  useEffect(() => {
    if (isSectionInView) {
      controlsDesktop.start("visible");
      controlsWheel.start("visible");
    }
  }, [isSectionInView, controlsDesktop, controlsWheel]);

  useEffect(() => {
    if (containerRef.current) {
      const containerHeight = (containerRef.current as HTMLDivElement)
        .offsetHeight;
      const numberOfSteps = 2;

      // Step size in pixels based on container height
      const stepSize = containerHeight / numberOfSteps + 1.11;

      const positions = Array.from(
        { length: numberOfSteps },
        (_, index) => stepSize * index,
      );
      setStepPositions(positions);
    }
  }, []);

  useEffect(() => {
    if (isMobileInView && stepPositions.length > 0) {
      const runAnimation = async () => {
        while (true) {
          setCurrentStep(0);
          await controlsMobile.start({
            y: `${stepPositions[0]}px`,
            transition: { duration: 1.5 },
          });

          await controlsMobile.start({
            y: `${stepPositions[1]}px`,
            rotate: 180,
            transition: { duration: 1.5, delay: 2 },
          });
          setCurrentStep(1);

          await controlsMobile.start({
            y: "0px",
            rotate: 0,
            transition: { duration: 0.8, ease: "easeInOut", delay: 2 },
          });
          setCurrentStep(-1);
        }
      };

      runAnimation();
    }
  }, [isMobileInView, controlsMobile, stepPositions]);

  useEffect(() => {
    if (isSectionInView) {
      controlsDesktop.start("visible");
    }
  }, [isSectionInView, controlsDesktop]);
  return (
    <div ref={sectionRef} id="faq">
      {isDashboard ? (
        <div className="max-lg:px-[12px]px-0 mb-[130px] mt-[36px] max-lg:mb-[56px]">
          <AnimatedGradientBorder className="rounded-[28px] max-lg:rounded-[24px] w-full">
            <GlowCard className="bg-[#111111] rounded-[28px] max-lg:rounded-[24px] before:!rounded-[28px] max-lg:before:!rounded-[24px] p-[12px] max-lg:p-[12px] flex flex-col gap-[12px] max-lg:gap-[8px]">
              <div
                className={clsx(
                  "flex h-[47px] max-lg:h-[47px] px-6 max-lg:px-[8px] items-center rounded-[16px] max-lg:rounded-[16px] before:!rounded-[16px] max-lg:before:!rounded-[16px]",
                  gradientRowBgClass,
                )}
              >
                <h2 className="text-[24px] max-lg:text-20m font-luxora-medium leading-[110%] text-white">
                  {t("FAQ")}
                </h2>
              </div>
              <Faqpage isDashboard={isDashboard} />
            </GlowCard>
          </AnimatedGradientBorder>
        </div>
      ) : (
        <Container
          sectionClasses="mb-[130px] mt-[36px] max-lg:mb-[56px]"
          animationControls={controlsDesktop}
          isNeedBg={false}
          fullWidth={isDashboard}
        >
          <SectionHeader
            title={t("FAQ")}
            sectionHeaderClasses="mb-[25px] max-lg:mb-[24px]"
          />
          <Faqpage isDashboard={isDashboard} />
        </Container>
      )}
    </div>
  );
};

export default FAQSection;
